import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { PaywallModal } from "@/components/PaywallModal";
import { useAuth } from "@/components/AuthContext";
import { paywallReasonFor, UPGRADE_EVENT, openUpgrade } from "@/lib/plans";
import { ensureHumanPass, HUMAN_CHECK_REQUIRED, TURNSTILE_SITE_KEY, needsProactiveCheck } from "@/lib/humanCheck";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Upgrade pop-up the moment a free try runs out (2026-09-28: "free plan
// everything one or two times ... then popup to upgrade").
//
// AI calls are made from many pages, not all through lib/aiErrors.js, so
// this watches responses app-wide: a 429 from an AI endpoint whose message
// is a monthly-quota / free-limit message (not a "too fast" rate limit)
// opens the plan picker. Signed-out visitors get the "sign up free" variant.
const AI_PATH = /\/api\/(ai|support\/chat|scrape-job)/;
const QUOTA_MSG = /You've used all|free AI limit|today's free AI limit|Sign up free/i;
// Plan-limit refusals (2026-09-29): saved-resume cap, locked template,
// share links, portfolio publishing, clean downloads -- all 402s whose
// message ends "Upgrade to <Plan> or higher."
const UPGRADE_MSG = /Upgrade (to|for) \w+|included with|paid plans?\b|plan includes/i;
export const QUOTA_EVENT = "rp360:quota-exceeded";

let installed = false;
function installFetchWatcher() {
  if (installed || typeof window === "undefined" || !window.fetch) return;
  installed = true;
  const orig = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const reqUrl = typeof args[0] === "string" ? args[0] : args[0]?.url || "";
    // Signed-out visitor about to make an AI call: run the human check
    // first, so the request goes out already verified.
    if (needsProactiveCheck(reqUrl)) {
      try { await ensureHumanPass(API_BASE, orig); } catch {}
    }
    let res = await orig(...args);
    // Real-human check (2026-10-01, common/human.py): an anonymous AI call
    // without a pass gets 403 human_check_required -- show the Turnstile
    // check, earn the pass cookie, and replay the same request once.
    if (res.status === 403 && TURNSTILE_SITE_KEY) {
      try {
        const d = await res.clone().json();
        if (d?.detail === HUMAN_CHECK_REQUIRED && (await ensureHumanPass(API_BASE, orig))) {
          res = await orig(...args);
        }
      } catch {}
    }
    try {
      const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "";
      // `rpSilent` (a client-only fetch option browsers ignore) marks a
      // best-effort background request whose 402 must not open the paywall.
      if (res.status === 402 && /\/api\//.test(url) && !args[1]?.rpSilent) {
        res.clone().json().then((d) => {
          const detail = typeof d?.detail === "string" ? d.detail : "";
          if (UPGRADE_MSG.test(detail)) {
            window.dispatchEvent(new CustomEvent(UPGRADE_EVENT, { detail: { reason: paywallReasonFor(url, detail), message: detail } }));
          }
        }).catch(() => {});
      }
      if (res.status === 429 && AI_PATH.test(url)) {
        res.clone().json().then((d) => {
          const detail = d?.detail || "";
          if (QUOTA_MSG.test(detail)) window.dispatchEvent(new CustomEvent(QUOTA_EVENT, { detail }));
        }).catch(() => {});
      }
    } catch {}
    return res;
  };
}

// Free-limit lock, narrowed 2026-10-04: originally (2026-10-01) any Free
// user who used one free try was locked out of the whole app -- which meant
// a Free user who generated their first resume could never see or download
// it. Now only signed-out visitors are locked (flag until the day ends,
// sign-up / log-in / payment / legal pages stay reachable); signed-in Free
// users get the normal closable picker when an AI quota runs out.
const ANON_LOCK_KEY = "rp360_anon_limit_until";
const LOCK_FREE_PATHS = /^\/(signup|login|reset-password|verify-email|payment-success|privacy|terms|refund|cookies|contact)(\/|$)/;

function anonLocked() {
  try { return Date.now() < Number(localStorage.getItem(ANON_LOCK_KEY) || 0); } catch { return false; }
}
function setAnonLock() {
  const end = new Date(); end.setHours(23, 59, 59, 999);
  try { localStorage.setItem(ANON_LOCK_KEY, String(end.getTime())); } catch {}
}

export function GlobalUpgradePrompt({ isLight }) {
  const { user, plan, loading } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("quota");
  const [limitHit, setLimitHit] = useState(false);

  // Signed-out visitors who used today's free tries stay locked (sign-up is
  // the way forward). Signed-in Free users are never locked: they keep
  // viewing, editing and downloading (watermarked); only AI actions past the
  // monthly quota are refused by the server, which opens the plan picker.
  useEffect(() => {
    if (loading) return;
    setLimitHit(!user && anonLocked());
  }, [user, loading]);

  useEffect(() => {
    installFetchWatcher();
    // Pages that already open their own plan picker keep theirs; this one
    // only steps in when nothing is showing.
    const show = (r) => setTimeout(() => {
      if (!document.querySelector('[data-testid="paywall-modal"]')) { setReason(r); setOpen(true); }
    }, 60);
    // Premium is the top plan (UAT 2026-10-03): a limit there is news, not an
    // upsell -- show the server's message ("resets on ...") instead of plans.
    const premiumNotice = (msg) => {
      if (window.__rp360Plan !== "premium" || !msg) return false;
      toast.info(msg);
      return true;
    };
    const onQuota = (e) => {
      if (premiumNotice(e.detail)) return;
      // Signed in (Free or paid): the normal, closable picker -- Free users
      // keep their resume, editor and watermarked download.
      if (window.__rp360SignedIn) { show("quota"); return; }
      // Signed-out visitors: out of free tries -> lock until they sign up.
      setLimitHit(true);
      setAnonLock();
    };
    const onUpgrade = (e) => {
      if (premiumNotice(e.detail?.message)) return;
      show(e.detail?.reason || "default");
    };
    window.addEventListener(QUOTA_EVENT, onQuota);
    window.addEventListener(UPGRADE_EVENT, onUpgrade);
    return () => {
      window.removeEventListener(QUOTA_EVENT, onQuota);
      window.removeEventListener(UPGRADE_EVENT, onUpgrade);
    };
  }, []);

  useEffect(() => { window.__rp360SignedIn = !!user; window.__rp360Plan = plan; }, [user, plan]);

  const locked = !loading && !user && limitHit && !LOCK_FREE_PATHS.test(pathname);
  if (locked) {
    return <PaywallModal open locked onClose={() => {}} isLight={isLight} reason="anonLimit" />;
  }
  return (
    <PaywallModal
      open={open}
      onClose={() => setOpen(false)}
      isLight={isLight}
      reason={!user && reason === "quota" ? "anon" : reason}
    />
  );
}

export { openUpgrade, installFetchWatcher };
